/**
 * Handles submenu positioning based on available viewport space
 * Automatically adjusts both vertical (top/bottom/center) and horizontal (left/right) positioning
 * to prevent submenus from being cut off by viewport edges.
 *
 * When neither side has enough horizontal space (e.g. mobile), switches to fixed positioning
 * with clamped viewport coordinates so the submenu is always visible.
 *
 * @param {HTMLElement} node - The submenu DOM node
 * @param {string} submenuKey - Unique key for the submenu
 * @param {Object} submenuRefs - Ref object to store submenu references
 */
export const handleSubmenuPositioning = (node, submenuKey, submenuRefs) => {
  submenuRefs.current[submenuKey] = node;

  if (!node) return;

  // Check position after DOM is ready
  setTimeout(() => {
    // Safety checks to prevent errors
    if (!node || !node.parentElement) {
      console.warn('Submenu node or parent not available for positioning');
      return;
    }

    try {
      const rect = node.getBoundingClientRect();
      const parentRect = node.parentElement.getBoundingClientRect();

      // Additional safety check for valid rectangles
      if (!rect || !parentRect || rect.height === 0) {
        console.warn('Invalid rectangles for submenu positioning');
        return;
      }

      let currentClass = node.className;

      // SPACE CALCULATIONS
      const spaceBelow = window.innerHeight - parentRect.bottom;
      const spaceAbove = parentRect.top;
      const submenuHeight = rect.height;
      const fitsBelow = spaceBelow >= submenuHeight;
      const fitsAbove = spaceAbove >= submenuHeight;

      const spaceRight = window.innerWidth - parentRect.right;
      const spaceLeft = parentRect.left;
      const submenuWidth = rect.width;
      const fitsOnRight = spaceRight >= submenuWidth;
      const fitsOnLeft = spaceLeft >= submenuWidth;

      if (!fitsOnRight && !fitsOnLeft) {
        // No horizontal space on either side (e.g. mobile) — switch to fixed positioning
        // so the submenu is always visible within the viewport.
        currentClass = currentClass
          .replace('left-full', '')
          .replace('right-full', '')
          .replace('-top-[14px]', '')
          .replace('-bottom-[8px]', '')
          .replace('top-1/2', '')
          .replace('-translate-y-1/2', '')
          .trim();

        node.style.position = 'fixed';
        node.style.right = 'unset';

        // Vertical: reuse same logic but with fixed (viewport) coordinates
        let fixedTop;
        if (!fitsBelow && !fitsAbove) {
          fixedTop = Math.max(0, (window.innerHeight - submenuHeight) / 2);
        } else if (!fitsBelow && fitsAbove) {
          fixedTop = Math.max(0, parentRect.top - submenuHeight);
        } else {
          fixedTop = parentRect.top;
        }
        node.style.top = `${fixedTop}px`;

        // Horizontal: pick the side with more space and clamp to viewport
        const preferRight = spaceRight >= spaceLeft;
        if (preferRight) {
          node.style.left = `${Math.max(0, Math.min(parentRect.right, window.innerWidth - submenuWidth))}px`;
        } else {
          node.style.left = `${Math.max(0, parentRect.left - submenuWidth)}px`;
        }
      } else {
        // Normal absolute positioning — fits on at least one side
        // Clear any inline styles left over from a previous fixed-positioning fallback
        node.style.position = '';
        node.style.top = '';
        node.style.left = '';
        node.style.right = '';

        // VERTICAL POSITIONING
        if (!fitsBelow && !fitsAbove) {
          // Doesn't fit either above or below - center it vertically
          currentClass = currentClass.replace('-top-[14px]', '').replace('-bottom-[8px]', '');
          if (!currentClass.includes('top-1/2')) {
            currentClass += ' top-1/2 -translate-y-1/2';
          }
        } else if (!fitsBelow && fitsAbove) {
          // Doesn't fit below but fits above - position above
          if (!currentClass.includes('-bottom-[8px]')) {
            currentClass = currentClass.replace('-top-[14px]', '-bottom-[8px]');
          }
          currentClass = currentClass.replace('top-1/2', '').replace('-translate-y-1/2', '').trim();
        } else {
          // Fits below (default) - position below
          if (!currentClass.includes('-top-[14px]')) {
            currentClass = currentClass.replace('-bottom-[8px]', '-top-[14px]');
          }
          currentClass = currentClass.replace('top-1/2', '').replace('-translate-y-1/2', '').trim();
        }

        // HORIZONTAL POSITIONING
        const isCurrentlyRight = currentClass.includes('left-full');
        const isCurrentlyLeft = currentClass.includes('right-full');

        let shouldSwitchToLeft = false;
        let shouldSwitchToRight = false;

        if (isCurrentlyRight && !fitsOnRight && fitsOnLeft) {
          shouldSwitchToLeft = true;
        } else if (isCurrentlyLeft && !fitsOnLeft && fitsOnRight) {
          shouldSwitchToRight = true;
        }

        if (shouldSwitchToLeft && !isCurrentlyLeft) {
          currentClass = currentClass.replace('left-full', 'right-full');
        } else if (shouldSwitchToRight && !isCurrentlyRight) {
          currentClass = currentClass.replace('right-full', 'left-full');
        }
      }

      node.className = currentClass;
    } catch (error) {
      console.warn('Error during submenu positioning:', error);
    }
  }, 0);
};

/**
 * Generates a unique key for submenu based on its path
 * @param {string} parentPath - Path from parent levels
 * @param {number} level - Current nesting level
 * @param {number} index - Index within current level
 * @returns {string} Unique submenu key
 */
export const generateSubmenuKey = (parentPath, level, index) => {
  return parentPath ? `${parentPath}-${index}` : `${level}-${index}`;
};
